import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import {
  useListItemRecipes,
  useCreateItemRecipe,
  useDeleteItemRecipe,
  useListInventoryItems,
  getListItemRecipesQueryKey,
  getListInventoryItemsQueryKey,
} from "@workspace/api-client-react";
import type { InventoryItemRecipe } from "@workspace/api-client-react";
import { specsToLine } from "@/lib/specs";
import { defaultQtyPerServe, stockOutHint, stockOutUnitLabel } from "@/lib/stock-out-basis";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface RecipeBuilderSectionProps {
  itemId: number;
  itemName: string;
}

interface AddComponentForm {
  componentItemId: number | null;
  qtyPerServe: string;
  // 2026-07-29: parity with the create-mode CreateRecipeRow builder, which has
  // always offered this. Without it, an item edited after creation could only
  // ever get 'tracked' components (the server default).
  consumptionMode: "tracked" | "estimated";
  notes: string;
}

export function RecipeBuilderSection({ itemId, itemName }: RecipeBuilderSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InventoryItemRecipe | null>(null);
  const [form, setForm] = useState<AddComponentForm>({
    componentItemId: null,
    qtyPerServe: "",
    consumptionMode: "tracked",
    notes: "",
  });
  const [comboOpen, setComboOpen] = useState(false);
  const [comboSearch, setComboSearch] = useState("");
  const [isPending, setIsPending] = useState(false);

  const { data: recipes = [], isLoading } = useListItemRecipes(itemId, {
    query: {
      queryKey: getListItemRecipesQueryKey(itemId),
      enabled: true,
    },
  });

  const { data: allItems = [] } = useListInventoryItems({ status: "Active" });

  const createMutation = useCreateItemRecipe();
  const deleteMutation = useDeleteItemRecipe();

  // Filter out:
  // 1. The served item itself (no self-reference)
  // 2. Items that are already used as components in this recipe (no duplicates)
  // Note: depth-1 invariant (no recipe items as components) is enforced server-side.
  // We do a best-effort filter by variantStockTotal == null (leaf items have no variant stock),
  // but ultimately the server enforces depth-1 via a 422 response.
  const existingComponentIds = new Set(recipes.map((r) => r.componentItemId));
  const candidateItems = allItems.filter(
    (it) => it.id !== itemId && !existingComponentIds.has(it.id) && !it.hasRecipe
  );

  const filteredCandidates = candidateItems.filter((it) =>
    it.name.toLowerCase().includes(comboSearch.toLowerCase())
  );

  const selectedItem = allItems.find((it) => it.id === form.componentItemId);

  function resetForm() {
    setForm({ componentItemId: null, qtyPerServe: "", consumptionMode: "tracked", notes: "" });
    setComboSearch("");
  }

  async function handleAddComponent() {
    if (!form.componentItemId) {
      toast({ title: "Select a component item", variant: "destructive" });
      return;
    }
    if (!form.qtyPerServe || isNaN(Number(form.qtyPerServe)) || Number(form.qtyPerServe) <= 0) {
      toast({ title: "Enter a valid quantity per serve (> 0)", variant: "destructive" });
      return;
    }
    setIsPending(true);
    try {
      await createMutation.mutateAsync({
        itemId,
        data: {
          componentItemId: form.componentItemId,
          qtyPerServe: form.qtyPerServe,
          consumptionMode: form.consumptionMode,
          notes: form.notes.trim() || null,
        },
      });
      queryClient.invalidateQueries({ queryKey: getListItemRecipesQueryKey(itemId) });
      // The item row carries hasRecipe / recipeCostTotal as correlated subqueries —
      // refresh the list so the Recipe pill and Margin reflect the new component.
      queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
      toast({ title: "Component added." });
      setAddOpen(false);
      resetForm();
    } catch (err: unknown) {
      // The dialog stays OPEN on failure so the operator never walks away
      // believing a rejected component was saved.
      const errAny = err as {
        status?: number;
        response?: { status?: number; data?: { error?: string; code?: string } };
      };
      const status = errAny.status ?? errAny.response?.status;
      const serverError = errAny.response?.data?.error;
      if (status === 409) {
        toast({ title: "This component is already part of this recipe.", variant: "destructive" });
      } else if (status === 422) {
        // 422 covers three distinct server guards (depth-1, cross-space,
        // incompatible charging model) — show the server's own message so the
        // operator knows which one fired instead of a hardcoded guess.
        toast({
          title: serverError ?? "Invalid component: recipe items cannot be used as components (depth-1 guard).",
          variant: "destructive",
        });
      } else {
        toast({
          title: serverError ?? "Failed to add component. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsPending(false);
    }
  }

  async function handleDeleteRecipe() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ itemId, recipeId: deleteTarget.id });
      queryClient.invalidateQueries({ queryKey: getListItemRecipesQueryKey(itemId) });
      queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
      toast({ title: "Component removed." });
    } catch {
      toast({ title: "Failed to remove component.", variant: "destructive" });
    } finally {
      setDeleteTarget(null);
    }
  }

  function getComponentName(componentItemId: number) {
    return allItems.find((it) => it.id === componentItemId)?.name ?? `Item #${componentItemId}`;
  }

  function getComponentUnit(componentItemId: number) {
    return allItems.find((it) => it.id === componentItemId)?.unitOfMeasurement ?? "";
  }

  // Ingredient currently selected in the add-component form, and the unit its
  // per-serve qty is counted in.
  const formSelectedItem = allItems.find((it) => it.id === form.componentItemId);
  const formUnitLabel = stockOutUnitLabel(formSelectedItem);

  return (
    <>
      <Separator className="my-4" />
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Recipe (prepared from components)</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add items consumed per serve. Stock_Out on &ldquo;{itemName}&rdquo; will decrement components, not this item itself.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => { resetForm(); setAddOpen(true); }}
          >
            + Add Component
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading recipe…</p>
        ) : recipes.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-md border border-dashed px-3 py-2">
            No recipe yet. Add components to make this item a recipe (e.g. Maggi needs Noodles + Milk + Oil).
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Component</TableHead>
                <TableHead>Qty per Serve</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipes.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{getComponentName(r.componentItemId)}</TableCell>
                  <TableCell className="font-mono text-xs">{Number(r.qtyPerServe).toFixed(3)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{getComponentUnit(r.componentItemId)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground capitalize">{r.consumptionMode ?? "tracked"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.notes ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add Component Dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); resetForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Recipe Component</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                Component Item <span className="text-destructive">*</span>
              </Label>
              <Popover open={comboOpen} onOpenChange={setComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    <span className={cn("truncate", !selectedItem && "text-muted-foreground")}>
                      {selectedItem ? (
                        <>
                          {selectedItem.name}
                          {selectedItem.brand ? ` · ${selectedItem.brand}` : ""}
                          {specsToLine(selectedItem.specs) ? ` · ${specsToLine(selectedItem.specs)}` : ""}
                          {" · "}({selectedItem.unitOfMeasurement})
                        </>
                      ) : "Search items…"}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by name…"
                      value={comboSearch}
                      onValueChange={setComboSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No items found.</CommandEmpty>
                      <CommandGroup>
                        {filteredCandidates.map((it) => {
                          const specs = specsToLine(it.specs);
                          // Same availability logic as ItemPicker so the recipe
                          // component picker reads consistent with the other pickers.
                          const availNum =
                            it.inventoryTracked === false
                              ? null
                              : (it as unknown as { variantStockTotal?: number | string | null }).variantStockTotal != null
                                ? Number((it as unknown as { variantStockTotal?: number | string | null }).variantStockTotal)
                                : it.currentStock != null
                                  ? Number(it.currentStock)
                                  : null;
                          const avail =
                            availNum != null && Number.isFinite(availNum) ? availNum : null;
                          return (
                            <CommandItem
                              key={it.id}
                              value={String(it.id)}
                              onSelect={() => {
                                // Re-seed the qty from the newly picked ingredient's own
                                // stock-out definition — see create-recipe-row.tsx.
                                setForm((f) => ({
                                  ...f,
                                  componentItemId: it.id,
                                  qtyPerServe: String(defaultQtyPerServe(it)),
                                }));
                                setComboSearch("");
                                setComboOpen(false);
                              }}
                              className="py-2"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4 shrink-0",
                                  form.componentItemId === it.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex min-w-0 flex-1 flex-col">
                                <span className="text-sm truncate">
                                  {it.name}
                                  {it.brand ? ` · ${it.brand}` : ""}
                                </span>
                                {(specs || it.unitOfMeasurement) && (
                                  <span className="text-[11px] text-muted-foreground truncate">
                                    {[specs, `(${it.unitOfMeasurement})`].filter(Boolean).join(" · ")}
                                  </span>
                                )}
                              </div>
                              {avail != null && (
                                <span
                                  className={cn(
                                    "ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                                    avail <= 0
                                      ? "bg-destructive/10 text-destructive"
                                      : "bg-emerald-50 text-emerald-700",
                                  )}
                                >
                                  {avail} in stock
                                </span>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Only stock items (not recipes) can be components. Recipe items are excluded automatically; if one slips through, the server will reject it.
              </p>
            </div>

            <div className="space-y-2">
              <Label>
                Qty per Serve <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.001"
                  min="0.001"
                  placeholder="e.g. 1.000"
                  className={cn(formUnitLabel && "pr-16")}
                  value={form.qtyPerServe}
                  onChange={(e) => setForm((f) => ({ ...f, qtyPerServe: e.target.value }))}
                />
                {formUnitLabel && (
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {formUnitLabel}
                  </span>
                )}
              </div>
              {formSelectedItem && stockOutHint(formSelectedItem) && (
                <p className="text-[11px] text-muted-foreground">{stockOutHint(formSelectedItem)}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Consumption Mode</Label>
              <div className="inline-flex rounded-md border bg-background p-0.5 text-xs">
                <button
                  type="button"
                  className={cn(
                    "px-3 py-1 rounded-sm transition-colors",
                    form.consumptionMode === "tracked"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => setForm((f) => ({ ...f, consumptionMode: "tracked" }))}
                >
                  Tracked
                </button>
                <button
                  type="button"
                  className={cn(
                    "px-3 py-1 rounded-sm transition-colors",
                    form.consumptionMode === "estimated"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => setForm((f) => ({ ...f, consumptionMode: "estimated" }))}
                >
                  Estimated
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {form.consumptionMode === "estimated"
                  ? "Known dependency, but stock is NOT auto-decremented on serve (e.g. ink per print)."
                  : "Decrements component stock on each serve."}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Optional — e.g. 'boiled before adding'"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setAddOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button type="button" onClick={handleAddComponent} disabled={isPending}>
              {isPending ? "Saving…" : "Add Component"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove component?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove{" "}
              <strong>{deleteTarget ? getComponentName(deleteTarget.componentItemId) : ""}</strong>{" "}
              from the recipe for &ldquo;{itemName}&rdquo;. Existing Stock_Out history is not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteRecipe}
              disabled={deleteMutation.isPending}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
